import { AsyncPipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  EditService,
  GridComponent,
  GridModule,
  IEditCell,
  PageService,
  SaveEventArgs,
  ToolbarService
} from '@syncfusion/ej2-angular-grids';
import { Observable } from 'rxjs';
import { TdRateFiduciary, TdRateFiduciaryToAddOrUpdate } from './td-rate-fiduciary.model';
import { Query } from '@syncfusion/ej2-data';
import { TdRatesFiduciariesService } from './td-rates-fiduciaries.service';
import { ApiErrorDialogComponent } from '../shared/api-error-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastComponent } from '../shared/toast.component';
import { SpinnerComponent } from '../shared/spinner.component';
import { CurrencyFootnoteEditorComponent } from '../shared/currency-footnote-editor.component';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'eaa-td-rates-fiduciaries',
  imports: [AsyncPipe, GridModule, FormsModule, SpinnerComponent, CurrencyFootnoteEditorComponent, ToastComponent],
  providers: [ToolbarService, EditService, PageService],
  template: `
    <div class="card">
      <div class="card-header">
        <h4 class="card-title m-0">TD RATES - FIDUCIARIES</h4>
      </div>
      <div class="card-body p-0">
        <ejs-grid
          [dataSource]="fiduciaries$ | async"
          [editSettings]="{
            allowEditing: true,
            allowAdding: true,
            allowDeleting: true,
            mode: 'Dialog'
          }"
          [toolbar]="['Add', 'Edit', 'Update', 'Delete', 'Cancel']"
          id="grid"
          #grid
          allowPaging="true"
          allowTextWrap="true"
          [pageSettings]="{ pageSizes: true, pageSize: 20 }"
          (actionBegin)="onActionBegin($event)"
          (actionComplete)="onActionComplete($event)"
        >
          <e-columns>
            <e-column field="id" headerText="#" width="70" [isPrimaryKey]="true" [allowEditing]="false" />
            <e-column field="name" headerText="Name" editType="stringedit" [validationRules]="{ required: true }" />
            <e-column
              field="region"
              headerText="Region"
              width="160"
              editType="dropdownedit"
              [edit]="regionEditParams"
              [validationRules]="{ required: true }"
            />
            <e-column
              field="location"
              headerText="Location"
              editType="stringedit"
              [validationRules]="{ required: true }"
            />
            <e-column field="creditRating" headerText="Credit Rating" editType="stringedit" />
            <e-column field="currencyFootnotes" headerText="Footnotes">
              <ng-template #template let-data>
                <div class="d-flex flex-column gap-1">
                  @for (footnote of data.currencyFootnotes; track $index) {
                    <span
                      ><strong>{{ footnote.currency }}:</strong> {{ footnote.footnote }}
                    </span>
                  }
                </div>
              </ng-template>
              <ng-template #editTemplate let-data>
                <eaa-currency-footnote-editor [(ngModel)]="editedFiduciary!.currencyFootnotes" />
              </ng-template>
            </e-column>
            <e-column field="comments" headerText="Comments" editType="stringedit" />
          </e-columns>
        </ejs-grid>
      </div>
    </div>

    <eaa-toast #toastTdRatesFiduciariesAction [showCloseButton]="false">
      <div id="title">PROCESSING</div>
      <div id="content" class="d-flex gap-2">
        <eaa-spinner [diameter]="20" />
        <div>
          {{ processingMessage }}
        </div>
      </div>
    </eaa-toast>
  `
})
export class TdRatesFiduciariesComponent implements OnInit {
  public fiduciaries$: Observable<TdRateFiduciary[]> | null = null;
  public editedFiduciary?: TdRateFiduciaryToAddOrUpdate;
  public processingMessage = '';

  public regionEditParams?: IEditCell = {
    params: { dataSource: ['Asia', 'Europe', 'MENA'], query: new Query(), actionComplete: () => false }
  };

  @ViewChild('grid')
  public grid?: GridComponent;

  @ViewChild('toastTdRatesFiduciariesAction')
  public toastTdRatesFiduciariesAction?: ToastComponent;

  constructor(
    private tdRatesFiduciariesService: TdRatesFiduciariesService,
    public apiErrorDialog: MatDialog
  ) {}

  public ngOnInit(): void {
    this.fiduciaries$ = this.tdRatesFiduciariesService.getTdRateFiduciaries$();
  }

  public onActionBegin(args: SaveEventArgs) {
    if (args.requestType === 'beginEdit' || args.requestType === 'add') {
      this.editedFiduciary = Object.assign({}, args.rowData) as TdRateFiduciaryToAddOrUpdate;
    }
    if (args.requestType === 'save') {
      (args.data as TdRateFiduciaryToAddOrUpdate).currencyFootnotes = this.editedFiduciary?.currencyFootnotes || [];
    }
  }

  public onActionComplete = (args: any) => {
    if (args.requestType === 'beginEdit' || args.requestType === 'add') {
      const dialog = (args as any).dialog;
      dialog.showCloseIcon = false;
      dialog.width = 900;
      dialog.header =
        args.requestType === 'beginEdit'
          ? `Edit fiduciary ${(args.rowData as TdRateFiduciaryToAddOrUpdate).name}`
          : 'New fiduciary';
    } else if (args.requestType === 'save') {
      const fiduciary = args.data as TdRateFiduciary;

      if (fiduciary.id) {
        this.processingMessage = `Updating TD rate fiduciary ${fiduciary.name}...`;
        this.toastTdRatesFiduciariesAction?.show();

        this.tdRatesFiduciariesService.updateTdRateFiduciary$(fiduciary.id, fiduciary).subscribe({
          next: (updated) => this.handleAfterExecute(true, `Updated TD rate fiduciary ${updated.name}`),
          error: (error) => this.handleAfterExecute(false, null, error)
        });
      } else {
        this.processingMessage = `Adding new TD rate fiduciary ${fiduciary.name}...`;
        this.toastTdRatesFiduciariesAction?.show();

        this.tdRatesFiduciariesService.insertTdRateFiduciary$(fiduciary).subscribe({
          next: (inserted) => this.handleAfterExecute(true, `Added new TD rate fiduciary ${inserted.name}`),
          error: (error) => this.handleAfterExecute(false, null, error)
        });
      }
    } else if (args.requestType === 'delete') {
      const fiduciaries = (args.data as TdRateFiduciary[]) || [];

      if (fiduciaries.length === 0) {
        this.resetToast();
        return;
      }

      this.processingMessage = `Deleting TD rate fiduciaries ${fiduciaries.map((f) => f.name).join(', ')}...`;
      this.toastTdRatesFiduciariesAction?.show();

      this.tdRatesFiduciariesService.deleteMultipleTdRateFiduciaries$(fiduciaries.map((f) => f.id)).subscribe({
        next: () =>
          this.handleAfterExecute(true, `Deleted TD rate fiduciaries ${fiduciaries.map((f) => f.name).join(', ')}`),
        error: (error) => this.handleAfterExecute(false, null, error)
      });
    }
  };

  private handleAfterExecute(
    success: boolean,
    processingMessage: string | null = null,
    error: any | null = null
  ): void {
    if (success) {
      this.fiduciaries$ = this.tdRatesFiduciariesService.getTdRateFiduciaries$();
      this.processingMessage = processingMessage || `Completed.`;

      setTimeout(() => {
        this.resetToast();
      }, 1500);
    } else {
      this.resetToast();

      if (error) {
        this.apiErrorDialog.open(ApiErrorDialogComponent, {
          width: '800px',
          data: error
        });
      }
    }
  }

  private resetToast() {
    this.toastTdRatesFiduciariesAction?.hide();
    this.processingMessage = '';
  }
}
